import { useContext } from 'react'
import { useLazySelector } from 'hooks'
import { ScheduleSimulatorSession } from 'modules/simulatorTrainings/common/components'
import { EnrolledPersonContext } from 'modules/simulatorTrainings/common/contexts'
import { useScheduleSessionState } from 'modules/simulatorTrainings/common/hooks'
import { useCheckCurrentMaintenancePeriod } from 'modules/simulatorTrainings/common/hooks'
import {
  SimulatorTrainingScheduleSession,
  SimulatorTrainingSessionType,
  WartsilaClassroomSessionState,
} from 'modules/simulatorTrainings/common/types'

type ScheduleSimulatorSessionContainerProps = {
  session: SimulatorTrainingScheduleSession
  sessionTypes: Record<SimulatorTrainingSessionType, string>
  onStartSimulation: (sessionId: Guid) => void
  onStopSimulation: (sessionId: Guid) => void
}

const ScheduleSimulatorSessionContainer: React.FC<
  ScheduleSimulatorSessionContainerProps
> = props => {
  const { session, onStartSimulation, sessionTypes, onStopSimulation } = props
  const { startDate, endDate, type: sessionType, sessionId } = session
  const isCurrentMaintenance = useCheckCurrentMaintenancePeriod()

  const isUserEnrolledPerson = useContext(EnrolledPersonContext)
  const { stopIsLoading, currentSessionStopped } = useLazySelector(
    ({ simulatorTrainingInstructor }) => {
      const { stopSimulatorTrainingSessionRequest, stoppedSimulatorTrainingSessions } =
        simulatorTrainingInstructor
      const { isLoading: stopIsLoading } = stopSimulatorTrainingSessionRequest

      return {
        stopIsLoading,
        currentSessionStopped: stoppedSimulatorTrainingSessions.includes(sessionId),
      }
    }
  )

  const currentSessionId = currentSessionStopped || !isUserEnrolledPerson ? undefined : sessionId
  const { sessionState, startingCountdown, isFailed } = useScheduleSessionState({
    sessionId: currentSessionId,
    startDate,
    endDate,
    sessionType,
  })

  return (
    <ScheduleSimulatorSession
      session={session}
      sessionTypes={sessionTypes}
      sessionState={sessionState as WartsilaClassroomSessionState}
      needHideActions={currentSessionStopped}
      stopIsLoading={stopIsLoading}
      onStartSimulation={onStartSimulation}
      onStopSimulation={onStopSimulation}
      sessionStateRequestCountdown={startingCountdown}
      isCurrentMaintenance={isCurrentMaintenance}
      isFailed={isFailed}
    />
  )
}

export default ScheduleSimulatorSessionContainer
